import { ComponentProps, useCallback } from 'react';
import { ConfirmDialog, NewConvergeButton } from '../../../presentation';
import {
  SubmissionStatus,
  useUpsertSubmissionMutation,
} from '@converge/graphql';
import { useConvergeSnackBar } from '../../../contexts';

import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';

interface IReferredSubmissionButtonProps
  extends ComponentProps<typeof LoadingButton> {
  referenceId?: string;
  submissionId: string;
}

export const HoldSubmissionButton = ({
  referenceId,
  submissionId,
  ...props
}: IReferredSubmissionButtonProps) => {
  const {
    info: { submissionStatus },
  } = useUnderwritingSubmissionStateContext();

  const { notify, closeSnackbar } = useConvergeSnackBar();
  const { upsertSubmission, loading: isUpdatingSubmission } =
    useUpsertSubmissionMutation({}, 'Overview');

  const [showHoldConfirmationModal, setShowHoldConfirmationModal] =
    useState<boolean>(false);

  const onHoldSubmissionConfirmationEvent = useCallback(
    async (submissionUuid: string) => {
      notify('Holding submission...', {
        variant: 'info',
      });

      closeSnackbar();
      setShowHoldConfirmationModal(false);

      try {
        await upsertSubmission({
          variables: {
            details: {
              uuid: submissionUuid,
              status:
                submissionStatus !== SubmissionStatus.Hold
                  ? SubmissionStatus.Hold
                  : SubmissionStatus.Reactivated,
            },
          },
        });

        closeSnackbar();

        notify(
          submissionStatus === SubmissionStatus.Hold
            ? 'Submission released successfully'
            : 'Submission put on hold successfully',
          {
            variant: 'success',
          }
        );
      } catch (cause) {
        closeSnackbar();
        notify(
          cause instanceof Error
            ? ` ${
                submissionStatus === SubmissionStatus.Hold
                  ? `Error releasing submission: ${cause.message}`
                  : `Error holding submission: ${cause.message}`
              }`
            : submissionStatus === SubmissionStatus.Hold
            ? 'Error releasing submission'
            : 'Error holding submission',
          {
            variant: 'error',
          }
        );
      }
    },
    [closeSnackbar, notify, submissionStatus, upsertSubmission]
  );

  return (
    <>
      <NewConvergeButton
        {...props}
        loading={isUpdatingSubmission}
        variant="contained"
        onClick={() => setShowHoldConfirmationModal(true)}
        label={
          submissionStatus === SubmissionStatus.Hold ? 'release hold' : 'hold'
        }
      />

      <ConfirmDialog
        show={showHoldConfirmationModal}
        title={'Hold submission'}
        content={`Are you sure you want to change status of submission ${referenceId} to ${
          submissionStatus === SubmissionStatus.Hold ? 'release' : 'hold'
        }?`}
        onCancel={() => setShowHoldConfirmationModal(false)}
        onConfirm={() => {
          onHoldSubmissionConfirmationEvent(submissionId);
        }}
      />
    </>
  );
};
